import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Fight } from '../../../../../../corelogic/domain/models/fight.model';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { FighterDisplayData } from '../../../../../../corelogic/domain/models/fighterDisplayData.model';
import { capitalizeFirstLetter } from '../../../../../../corelogic/domain/utils/capitalize.utils';
import {
  determineFightResult,
  formatDateWithLeadingZeros,
} from '../../../../../../corelogic/domain/utils/fight.utils';
import {
  calculateAge,
  convertToFeetInches,
  convertToInches,
} from '../../../../../../corelogic/domain/utils/fighter.utils';
import { getDefaultFighterImage } from '../../../../../../corelogic/domain/utils/fighterImage.utils';
import { getWeightDisplayName } from '../../../../../../corelogic/domain/utils/weightUtils';
import { useFighterImageState } from '../../../hooks/useFighterImage';
import { FighterImageService } from '../../../services/FighterImage.service';
import FightResultCircle from '../FightResultCircle/FightResultCircle';

const titleFightStyle = {
  '&:nth-of-type(odd)': { bgcolor: 'rgba(255, 215, 0, 0.08)' },
  '&:nth-of-type(even)': { bgcolor: 'rgba(255, 215, 0, 0.05)' },
  borderLeft: '3px solid rgba(255, 215, 0, 0.7)',
};

const regularFightStyle = {
  '&:nth-of-type(odd)': { bgcolor: 'rgba(40, 40, 40, 0.7)' },
  '&:nth-of-type(even)': { bgcolor: 'inherit' },
  borderLeft: 'none',
};

// Styles
const styles = {
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'rgba(25, 25, 25, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: 4,
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  fighterAvatar: {
    width: 80,
    height: 80,
    mr: 2,
    borderRadius: '50%',
    border: '3px solid white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
  },
  fighterNameContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  fighterName: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    color: 'white',
  },
  nickname: {
    fontStyle: 'italic',
    color: '#cc0000',
    mb: 1,
  },
  divider: {
    bgcolor: 'rgba(255, 255, 255, 0.2)',
    my: 2,
  },
  detailsContainer: {
    mb: 2,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    mb: 1,
  },
  statLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  statValue: {
    color: 'white',
    fontSize: 18,
    fontFamily: 'Bebas Neue',
    fontWeight: 'normal',
  },
  sectionTitle: {
    fontFamily: 'Bebas Neue',
    color: 'white',
    fontSize: 24,
    mb: 1,
  },
  tableContainer: {
    bgcolor: 'rgba(30, 30, 30, 0.7)',
    mb: 2,
  },
  tableContainerWithScroll: {
    bgcolor: 'rgba(30, 30, 30, 0.7)',
    maxHeight: 250,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.4)',
      },
    },
  },
  tableHeader: {
    fontWeight: 'bold',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
  },
  tableCell: {
    color: 'white',
    fontSize: 14,
  },
  noDataText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontStyle: 'italic',
    my: 2,
  },
  titleFightIcon: {
    component: 'span',
    ml: 0.2,
    fontSize: '0.7rem',
    color: '#FFD700',
  },
  loadMoreButton: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

interface FighterDetailsModalProps {
  open: boolean;
  onClose: () => void;
  fighter: Fighter | null;
  fights: Fight[];
}

const FighterDetailsModal: React.FC<FighterDetailsModalProps> = ({
  open,
  onClose,
  fighter,
  fights: allFights,
}) => {
  // Get past fights for this fighter
  const pastFights = useMemo(() => {
    if (!fighter || !allFights) return [];
    return allFights
      .filter(
        (fight) =>
          (fight.fighter_1_id === fighter.id ||
            fight.fighter_2_id === fighter.id) &&
          new Date(fight.date) < new Date(),
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allFights, fighter]);

  const [displayCount, setDisplayCount] = useState(5);

  // Get the first past fights to display
  const visiblePastFights = useMemo(() => {
    return pastFights.slice(0, displayCount);
  }, [pastFights, displayCount]);

  // Get upcoming fights
  const upcomingFights = useMemo(() => {
    if (!fighter || !allFights) return [];
    return allFights
      .filter(
        (fight) =>
          (fight.fighter_1_id === fighter.id ||
            fight.fighter_2_id === fighter.id) &&
          new Date(fight.date) >= new Date(),
      )
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [allFights, fighter]);

  const { fighterImage, isLoading } = useFighterImageState(
    fighter?.firstname,
    fighter?.lastname,
    fighter?.dob,
  );

  // Request fighter image when component mounts or fighter changes
  useEffect(() => {
    if (fighter && fighter.dob) {
      FighterImageService.retrieveFighterImage(
        fighter.firstname,
        fighter.lastname,
        fighter.dob,
        fighter.gender,
      );
    }
  }, [fighter]);

  // Early return if no fighter - AFTER all hooks are called
  if (!fighter) {
    return null;
  }

  // Prepare fighter data for display
  const fighterDisplay = (fighter: Fighter): FighterDisplayData => {
    return {
      id: fighter.id,
      fullName: [fighter.firstname, fighter.lastname].filter(Boolean).join(' '),
      nickname: fighter.nickname || null,
      recordDisplay: `${fighter.wins}-${fighter.losses}-${fighter.draws}`,
      weightClass: getWeightDisplayName(fighter.weight) || 'Unknown',
      heightDisplay: convertToFeetInches(fighter.height) || 'Unknown',
      reachDisplay: convertToInches(fighter.reach) || 0,
      stance: fighter.stance || 'Unknown',
      ageDisplay: fighter.dob ? calculateAge(fighter.dob.toString()) : 0,
      gender: fighter.gender || 'Unknown',
      profileImage: fighterImage || getDefaultFighterImage(fighter.gender),
    };
  };
  const fighterDisplayData = fighterDisplay(fighter);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modalContainer}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
        {/* Fighter Header */}
        <Box sx={styles.headerContainer}>
          {isLoading ? (
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              animation="wave"
              sx={{
                mr: 2,
                border: '3px solid white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
              }}
            />
          ) : (
            <Avatar
              src={fighterDisplayData.profileImage}
              alt={fighterDisplayData.fullName}
              sx={styles.fighterAvatar}
            />
          )}
          <Box sx={styles.fighterNameContainer}>
            <Typography sx={styles.fighterName}>
              {fighterDisplayData.fullName}
            </Typography>
            {fighterDisplayData.nickname && (
              <Typography sx={styles.nickname}>
                "{fighterDisplayData.nickname}"
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={styles.divider} />

        {/* Fighter Details */}
        <Grid container spacing={2} sx={styles.detailsContainer}>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Record (W-L-D)</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.recordDisplay}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Division</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.weightClass}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Height</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.heightDisplay}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Reach</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.reachDisplay} ''
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Stance</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.stance}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Age</Typography>
              <Typography sx={styles.statValue}>
                {fighterDisplayData.ageDisplay}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={styles.statItem}>
              <Typography sx={styles.statLabel}>Gender</Typography>
              <Typography sx={styles.statValue}>
                {capitalizeFirstLetter(fighterDisplayData.gender)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={styles.divider} />

        {/* Upcoming Fights */}
        <Typography sx={styles.sectionTitle}>Upcoming Fights</Typography>
        {upcomingFights.length > 0 ? (
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>Date</TableCell>
                  <TableCell sx={styles.tableHeader}>Opponent</TableCell>
                  <TableCell sx={styles.tableHeader}>Event</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcomingFights.map((fight) => {
                  const isFirstFighter = fight.fighter_1_id === fighter.id;
                  const opponent = isFirstFighter
                    ? fight.fighter_2
                    : fight.fighter_1;
                  const isTitleFight = fight.title_fight === 'T';
                  return (
                    <TableRow
                      key={fight.id}
                      sx={isTitleFight ? titleFightStyle : regularFightStyle}
                    >
                      <TableCell sx={styles.tableCell}>
                        {formatDateWithLeadingZeros(fight.date)}
                        {isTitleFight && (
                          <Box sx={styles.titleFightIcon}>👑</Box>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>{opponent}</TableCell>
                      <TableCell sx={styles.tableCell}>{fight.name}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={styles.noDataText}>
            No upcoming fights scheduled
          </Typography>
        )}

        <Divider sx={styles.divider} />

        {/* Past Fights */}
        <Typography sx={styles.sectionTitle}>Recent Fights</Typography>
        {pastFights.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={styles.tableContainerWithScroll}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>Date</TableCell>
                  <TableCell sx={styles.tableHeader}>Opponent</TableCell>
                  <TableCell sx={styles.tableHeader}>Event</TableCell>
                  <TableCell sx={styles.tableHeader} align="center">
                    Result
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiblePastFights.map((fight) => {
                  const isFirstFighter = fight.fighter_1_id === fighter.id;
                  const opponent = isFirstFighter
                    ? fight.fighter_2
                    : fight.fighter_1;
                  const result = determineFightResult(fight, fighter.id);
                  const isTitleFight = fight.title_fight === 'T';
                  return (
                    <TableRow
                      key={fight.id}
                      sx={isTitleFight ? titleFightStyle : regularFightStyle}
                    >
                      <TableCell sx={styles.tableCell}>
                        {formatDateWithLeadingZeros(fight.date)}
                        {isTitleFight && (
                          <Box sx={styles.titleFightIcon}>👑</Box>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>{opponent}</TableCell>
                      <TableCell sx={styles.tableCell}>{fight.name}</TableCell>
                      <TableCell sx={styles.tableCell} align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <FightResultCircle
                            result={result}
                            isTitleFight={isTitleFight}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={styles.noDataText}>No recent fights found</Typography>
        )}
        {pastFights.length > displayCount && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Button
              size="small"
              onClick={() => setDisplayCount((prev) => prev + 10)}
              sx={styles.loadMoreButton}
            >
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FighterDetailsModal;
